import React, { useState } from 'react';
import styled from 'styled-components';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';

import { sendPasswordReset } from '../../Utils/authEmailActions';
import { useI18n } from '../../i18n/I18nProvider';

const friendlyError = (error, pick) => {
  const code = String(error?.code || '');
  if (code.includes('too-many-requests')) return pick(
    'Trop de demandes ont été envoyées. Réessayez dans quelques minutes.',
    'Too many requests were sent. Try again in a few minutes.',
  );
  if (code.includes('user-not-found')) return pick(
    'Aucun compte ne correspond à cette adresse.',
    'No account matches this email address.',
  );
  return pick(
    'Le lien de changement de mot de passe n’a pas pu être envoyé.',
    'The password reset link could not be sent.',
  );
};

const ProfileSecurityCard = ({ email }) => {
  const { pick } = useI18n();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const sendPasswordLink = async () => {
    if (!email || sending) return;
    setSending(true);
    setMessage('');
    setError('');
    try {
      await sendPasswordReset(email, '/login?passwordReset=1');
      setMessage(pick(`Lien envoyé à ${email}.`, `Link sent to ${email}.`));
    } catch (nextError) {
      setError(friendlyError(nextError, pick));
    } finally {
      setSending(false);
    }
  };

  return (
    <SecurityContent>
      <Heading>
        <SecurityOutlinedIcon />
        <div>
          <h2>{pick('Connexion et sécurité', 'Sign-in and security')}</h2>
          <p>{pick('Les informations du profil se sauvegardent sans vous redemander votre mot de passe.', 'Profile information can be saved without asking for your password again.')}</p>
        </div>
      </Heading>

      <EmailBox>
        <div>
          <span>{pick('Adresse de connexion', 'Sign-in email')}</span>
          <strong>{email || pick('Adresse indisponible', 'Email unavailable')}</strong>
        </div>
        <MarkEmailReadOutlinedIcon />
      </EmailBox>

      <PasswordRow>
        <div>
          <strong>{pick('Changer mon mot de passe', 'Change my password')}</strong>
          <span>{pick('Nous envoyons un lien sécurisé. Aucun mot de passe n’est saisi dans le formulaire du profil.', 'We send a secure link. Your password is never entered in the profile form.')}</span>
        </div>
        <ResetButton type="button" onClick={sendPasswordLink} disabled={!email || sending}>
          <LockResetOutlinedIcon /> {sending ? pick('Envoi…', 'Sending…') : pick('Recevoir le lien', 'Send me the link')}
        </ResetButton>
      </PasswordRow>

      {message && <Feedback $success role="status">{message}</Feedback>}
      {error && <Feedback role="alert">{error}</Feedback>}
    </SecurityContent>
  );
};

const SecurityContent = styled.div`display:grid;gap:.85rem;`;
const Heading = styled.header`
  display:flex;align-items:flex-start;gap:.65rem;padding-bottom:.75rem;border-bottom:1px solid #eeebe6;
  >svg{width:2.25rem;height:2.25rem;padding:.5rem;border-radius:.7rem;background:#111;color:#fff;}
  div{display:grid;gap:.15rem;}h2{margin:0;font-size:1.02rem;}p{margin:0;color:#756f67;font-size:.72rem;line-height:1.45;}
`;
const EmailBox = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:.75rem;padding:.8rem .9rem;border:1px solid #e4ddd2;border-radius:.85rem;background:#f8f6f2;
  div{display:grid;gap:.14rem;min-width:0;}span{color:#756f67;font-size:.65rem;}strong{font-size:.78rem;overflow-wrap:anywhere;}svg{color:#25744a;flex:0 0 auto;}
`;
const PasswordRow = styled.div`
  display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:.75rem;
  >div{display:grid;gap:.16rem;}strong{font-size:.78rem;}span{color:#756f67;font-size:.66rem;line-height:1.45;}
  @media(max-width:38rem){grid-template-columns:1fr;}
`;
const ResetButton = styled.button`
  min-height:2.65rem;display:inline-flex;align-items:center;justify-content:center;gap:.35rem;padding:.58rem .78rem;border:1px solid #111;border-radius:999px;background:#fff;color:#111;font:inherit;font-size:.7rem;font-weight:850;cursor:pointer;
  svg{font-size:1rem;}&:disabled{opacity:.55;cursor:wait;}@media(max-width:38rem){width:100%;}
`;
const Feedback = styled.p`
  margin:0;padding:.6rem .7rem;border-radius:.7rem;background:${({$success})=>$success?'#edf8f1':'#fff0ef'};color:${({$success})=>$success?'#17623a':'#9a302a'};font-size:.68rem;line-height:1.45;
`;

export default ProfileSecurityCard;
